import type { RiskProfile } from '@/features/clients/types/schemas';
import type { ConcernKey } from '@/features/sessions/constants/concerns';
import { ApiError } from '@/lib/errors';

export interface LeaveAndCompleteInput {
  concerns: ConcernKey[];
  /** Only present when the practitioner explicitly changed the client's risk. */
  risk?: RiskProfile;
}

/**
 * The BE status guard for a row that is already `completed`. Older API builds
 * reject a completed → completed PATCH with this 400; newer ones accept it as
 * a wrap-up. Matched on the message because the guard shares its 400 status
 * with unrelated validation errors (concerns gate, risk gate).
 */
export function isAlreadyCompletedError(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 400 &&
    /cannot change status from completed/i.test(err.message)
  );
}

/**
 * Orchestrates "Leave & complete" from the call screen.
 *
 * Normal path: PATCH status=completed (concerns + optional risk), then tear
 * the LiveKit room down. The status PATCH enforces the BE concerns / risk
 * gates, so any other error from it propagates to the dialog untouched and
 * the call stays live.
 *
 * Already-completed path: the scheduledEnd sweep (or another practitioner
 * action) completed the row while the call was still live. Against an API
 * that still rejects the re-completion, keep the practitioner's wrap-up by
 * retagging concerns through the completed-only endpoint, report anything
 * that could not be saved, and still end the call. Never leave the
 * practitioner stuck in a room whose session is already closed.
 */
export async function runLeaveAndComplete({
  input,
  updateStatus,
  updateConcerns,
  endCall,
  onCallEndFailed,
  onWrapUpNotSaved,
}: {
  input: LeaveAndCompleteInput;
  updateStatus: (input: LeaveAndCompleteInput) => Promise<unknown>;
  updateConcerns: (concerns: ConcernKey[]) => Promise<unknown>;
  endCall: () => Promise<unknown>;
  onCallEndFailed: () => void;
  onWrapUpNotSaved: (input: { what: 'concerns' | 'risk' }) => void;
}): Promise<void> {
  try {
    await updateStatus(input);
  } catch (err) {
    if (!isAlreadyCompletedError(err)) throw err;
    if (input.concerns.length > 0) {
      try {
        await updateConcerns(input.concerns);
      } catch {
        onWrapUpNotSaved({ what: 'concerns' });
      }
    }
    if (input.risk !== undefined) onWrapUpNotSaved({ what: 'risk' });
  }

  try {
    await endCall();
  } catch {
    // Room teardown failure is non-fatal: the session is completed
    // server-side and LiveKit's empty timeout closes the room.
    onCallEndFailed();
  }
}
